// ============================================================
// BuscadorMatricula — Input DNI + lookup contra el backend.
// Devuelve la matrícula activa del estudiante para crear citas
// y derivaciones. Reusable.
// ============================================================

import React, { useState } from 'react';
import { I, UserAvatar } from 'components/ui';
import { matriculaService } from 'services/matriculaService';
import { MatriculaResponse } from 'types/matricula.types';

interface Props {
  anioEscolarId?: number;
  onSelected:    (m: MatriculaResponse) => void;
  onClear?:      () => void;
  selected?:     MatriculaResponse | null;
}

const Spinner: React.FC = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const BuscadorMatricula: React.FC<Props> = ({ anioEscolarId, onSelected, onClear, selected }) => {
  const [dni, setDni] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buscar = async () => {
    if (!/^\d{8}$/.test(dni)) {
      setError('Ingresa un DNI válido de 8 dígitos.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const m = await matriculaService.buscarPorDni(dni, anioEscolarId);
      if (!m) {
        setError('No se encontró matrícula activa con ese DNI.');
        return;
      }
      onSelected(m);
      setDni('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al buscar.');
    } finally {
      setLoading(false);
    }
  };

  if (selected) {
    return (
      <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <UserAvatar
          idUsuario={selected.estudianteId}
          fotoPerfil={null}
          nombres={selected.estudianteNombres}
          apellidos={selected.estudianteApellidos}
          size={36}
          rounded="xl"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800 truncate">
            {selected.estudianteApellidos}, {selected.estudianteNombres}
          </p>
          <p className="text-xs text-slate-500">
            DNI {selected.estudianteDni} · {selected.gradoNombre} {selected.seccionNombre} · {selected.anio}
          </p>
        </div>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-slate-500 hover:text-red-600 font-semibold"
          >
            Cambiar
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          maxLength={8}
          value={dni}
          onChange={e => { setDni(e.target.value.replace(/\D/g, '')); setError(null); }}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), buscar())}
          placeholder="DNI del estudiante (8 dígitos)"
          className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white tabular-nums tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        />
        <button
          type="button"
          onClick={buscar}
          disabled={loading || dni.length !== 8}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold disabled:opacity-60"
        >
          {loading ? <Spinner /> : <I.Search size={14} />}
          Buscar
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

export default BuscadorMatricula;
